import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type {
  ApiResponse,
  BranchOperatingFunds,
  BranchOperatingFundsUpsertPayload,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';

/**
 * Sub-recurso de umbral / fondo operativo de sucursal.
 *
 * Endpoints:
 *   - GET `/api/structure/branches/{branchId}/operating-funds`
 *   - PUT `/api/structure/branches/{branchId}/operating-funds` (multipart)
 *
 * GET típico (vacío):
 *   `{ branchId, coinId: null, files: [] }`
 *
 * PUT: `coinId` obligatorio, opcionales `mandatory` / `restrictive` /
 * `amount` / `observations`, y pares `files` + `orders`.
 */
@Injectable({ providedIn: 'root' })
export class BranchOperatingFundsService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/branches`;
  }

  get(branchId: string): Observable<BranchOperatingFunds> {
    return this.httpClient
      .get<ApiResponse<BranchOperatingFunds | null>>(
        `${this.apiUrl}/${branchId}/operating-funds`,
      )
      .pipe(
        map((response) => ({
          branchId: response.data?.branchId || branchId,
          mandatory: response.data?.mandatory,
          restrictive: response.data?.restrictive,
          amount: response.data?.amount ?? null,
          observations: response.data?.observations ?? null,
          coinId: response.data?.coinId ?? null,
          files: Array.isArray(response.data?.files)
            ? response.data!.files
            : [],
        })),
      );
  }

  update(
    branchId: string,
    payload: BranchOperatingFundsUpsertPayload,
  ): Observable<BranchOperatingFunds> {
    return this.httpClient
      .put<ApiResponse<BranchOperatingFunds | null>>(
        `${this.apiUrl}/${branchId}/operating-funds`,
        this.toFormData(payload),
      )
      .pipe(
        map((response) => ({
          branchId: response.data?.branchId || branchId,
          mandatory: response.data?.mandatory ?? payload.mandatory,
          restrictive: response.data?.restrictive ?? payload.restrictive,
          amount: response.data?.amount ?? payload.amount ?? null,
          observations:
            response.data?.observations ?? payload.observations ?? null,
          coinId: response.data?.coinId ?? payload.coinId ?? null,
          files: Array.isArray(response.data?.files)
            ? response.data!.files
            : [],
        })),
      );
  }

  private toFormData(payload: BranchOperatingFundsUpsertPayload): FormData {
    const formData = new FormData();

    if (payload.mandatory != null) {
      formData.append('mandatory', String(payload.mandatory));
    }
    if (payload.restrictive != null) {
      formData.append('restrictive', String(payload.restrictive));
    }
    if (payload.amount != null && payload.amount !== '') {
      formData.append('amount', String(payload.amount));
    }
    if (payload.observations != null && payload.observations !== '') {
      formData.append('observations', payload.observations);
    }

    formData.append('coinId', payload.coinId);

    const files: File[] = payload.files ?? [];
    const orders: number[] = payload.orders ?? [];
    files.forEach((file, index) => {
      formData.append('files', file);
      formData.append('orders', String(orders[index] ?? index + 1));
    });

    return formData;
  }
}
